/**
 * Bulk import for the Cloudflare edge blocklist. The client pastes their full
 * Google-Ads-format list (exact IPv4, IPv4 CIDR/wildcard, IPv6, /64…); we validate
 * it server-side (parse + dedup + whitelist + already-blocked), show an honest
 * summary, then block the ready set in chunks with a visible progress bar. A failing
 * chunk never aborts the rest — the run resumes to the end and reports the tally.
 */
import { useTranslation } from '@silospot/i18n/react';
import type { BulkValidateResult } from '@silospot/shared';
import { toast } from '@silospot/ui';
import { Loader2 } from 'lucide-react';
import { type ReactElement, useState } from 'react';
import { api, serverError } from '../../lib/api';
import { Btn, Card, CardHead } from './kit';

/** Cloudflare caps IP Access Rules at 50,000 per account — the truthful ceiling. */
const CF_CAP = 50_000;
/** The bulk-exec endpoint accepts ≤ 50 targets per call; the UI loops chunks. */
const CHUNK = 50;

interface Props {
  projectId: string;
  listCount: number;
  onDone: () => void;
}

export function CfBulkImport({ projectId, listCount, onDone }: Props): ReactElement {
  const { t } = useTranslation('pages');
  const [raw, setRaw] = useState('');
  const [preview, setPreview] = useState<BulkValidateResult | null>(null);
  const [validating, setValidating] = useState(false);
  const [running, setRunning] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0, ok: 0, fail: 0 });

  const validate = async () => {
    if (!raw.trim()) {
      toast.error(t('cfp.bulkEmpty'));
      return;
    }
    setValidating(true);
    try {
      setPreview(await api.cfBulkValidate(projectId, raw));
    } catch (e) {
      toast.error(serverError(e) ?? t('cf.errorGeneric'));
    } finally {
      setValidating(false);
    }
  };

  const reset = () => {
    setPreview(null);
    setRaw('');
    setProgress({ done: 0, total: 0, ok: 0, fail: 0 });
  };

  const run = async () => {
    if (!preview || preview.valid.length === 0) return;
    const targets = preview.valid.map((v) => v.target);
    setRunning(true);
    setProgress({ done: 0, total: targets.length, ok: 0, fail: 0 });
    let ok = 0;
    let fail = 0;
    for (let i = 0; i < targets.length; i += CHUNK) {
      const chunk = targets.slice(i, i + CHUNK);
      try {
        const r = await api.cfBulkBlock(projectId, chunk);
        for (const res of r.results) {
          if (res.ok) ok += 1;
          else fail += 1;
        }
      } catch {
        // A chunk-level failure (network/500) counts the whole chunk as failed but
        // does not abort — the next chunks still run.
        fail += chunk.length;
      }
      setProgress({
        done: Math.min(i + chunk.length, targets.length),
        total: targets.length,
        ok,
        fail,
      });
    }
    setRunning(false);
    toast.success(t('cfp.bulkDoneToast', { ok, fail }));
    onDone();
    reset();
  };

  const projected = listCount + (preview?.valid.length ?? 0);
  const overCap = projected > CF_CAP;

  return (
    <Card className="mb-4">
      <CardHead title={t('cfp.bulkTitle')} sub={t('cfp.bulkSub')} />

      {/* Paste zone — hidden once a preview is on screen to keep the flow linear. */}
      {!preview ? (
        <div className="space-y-3">
          <textarea
            value={raw}
            onChange={(e) => setRaw(e.target.value)}
            spellCheck={false}
            rows={7}
            placeholder={t('cfp.bulkPlaceholder')}
            className="w-full resize-y rounded-xl border border-border bg-bg/40 px-3 py-2.5 font-mono text-[12.5px] leading-relaxed text-text outline-none focus:border-accent-strong"
          />
          <div className="flex items-center justify-between gap-3">
            <span className="text-[11.5px] text-text-3">
              {t('cfp.bulkCap', {
                n: listCount.toLocaleString('fr'),
                cap: CF_CAP.toLocaleString('fr'),
              })}
            </span>
            <Btn primary disabled={validating} onClick={validate}>
              {validating ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              {t('cfp.bulkValidate')}
            </Btn>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          {/* Honest, categorised summary. */}
          <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-4">
            <SummaryTile label={t('cfp.bulkValidCount')} n={preview.counts.valid} tone="fraud" />
            <SummaryTile
              label={t('cfp.bulkDupCount')}
              n={preview.counts.alreadyBlocked}
              tone="neutral"
            />
            <SummaryTile label={t('cfp.bulkWlCount')} n={preview.counts.whitelisted} tone="clean" />
            <SummaryTile
              label={t('cfp.bulkInvalidCount')}
              n={preview.counts.invalid}
              tone="neutral"
            />
          </div>

          {/* Protected entries are never blocked — name them so the choice is visible. */}
          {preview.whitelisted.length > 0 ? (
            <p className="text-[12px] leading-relaxed text-clean-fg">
              {t('cfp.bulkWlNote')}{' '}
              <span className="font-mono">{preview.whitelisted.slice(0, 8).join(', ')}</span>
              {preview.whitelisted.length > 8 ? ` +${preview.whitelisted.length - 8}` : ''}
            </p>
          ) : null}

          {/* Rejected lines with their plain reason — collapsed detail, first 12. */}
          {preview.invalid.length > 0 ? (
            <details className="rounded-xl border border-border bg-bg/40 px-3.5 py-2.5">
              <summary className="cursor-pointer text-[12.5px] font-semibold text-text-2">
                {t('cfp.bulkInvalidList', { n: preview.invalid.length })}
              </summary>
              <ul className="mt-2 space-y-1">
                {preview.invalid.slice(0, 12).map((iv, i) => (
                  <li key={`${iv.input}-${i}`} className="text-[12px] text-text-3">
                    <span className="font-mono text-text-2">{iv.input}</span> — {iv.reason}
                  </li>
                ))}
                {preview.invalid.length > 12 ? (
                  <li className="text-[12px] text-text-3">
                    {t('cfp.bulkMore', { n: preview.invalid.length - 12 })}
                  </li>
                ) : null}
              </ul>
            </details>
          ) : null}

          {overCap ? (
            <p className="rounded-xl border border-[#f3c9c9] bg-fraud-bg px-3.5 py-2.5 text-[12px] leading-relaxed text-fraud-fg">
              {t('cfp.bulkCapWarn', { cap: CF_CAP.toLocaleString('fr') })}
            </p>
          ) : null}

          {/* Live progress during the run. */}
          {running ? (
            <div className="space-y-1.5">
              <div className="h-2 overflow-hidden rounded-full bg-neutral-bg">
                <div
                  className="h-full rounded-full bg-accent-strong transition-[width] duration-200"
                  style={{
                    width: `${progress.total ? (progress.done / progress.total) * 100 : 0}%`,
                  }}
                />
              </div>
              <div className="text-[11.5px] text-text-3">
                {t('cfp.bulkRunning', { done: progress.done, total: progress.total })}
              </div>
            </div>
          ) : null}

          <div className="flex flex-wrap items-center justify-between gap-3">
            <span className="text-[11.5px] text-text-3">
              {t('cfp.bulkCap', {
                n: projected.toLocaleString('fr'),
                cap: CF_CAP.toLocaleString('fr'),
              })}
            </span>
            <div className="flex gap-2">
              <Btn disabled={running} onClick={reset}>
                {t('cfp.bulkReset')}
              </Btn>
              <Btn
                primary
                disabled={running || overCap || preview.valid.length === 0}
                onClick={run}
              >
                {running ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                {preview.valid.length === 0
                  ? t('cfp.bulkNothing')
                  : t('cfp.bulkConfirm', { n: preview.valid.length })}
              </Btn>
            </div>
          </div>
        </div>
      )}
    </Card>
  );
}

function SummaryTile({
  label,
  n,
  tone,
}: {
  label: string;
  n: number;
  tone: 'fraud' | 'clean' | 'neutral';
}): ReactElement {
  const cls =
    tone === 'fraud'
      ? 'border-[#f3c9c9] bg-fraud-bg text-fraud-fg'
      : tone === 'clean'
        ? 'border-clean-border bg-clean-bg text-clean-fg'
        : 'border-border bg-bg/40 text-text-2';
  return (
    <div className={`rounded-lg border px-3 py-2.5 ${cls}`}>
      <div className="text-[17px] font-bold leading-none">{n}</div>
      <div className="mt-1 text-[11px] leading-tight">{label}</div>
    </div>
  );
}
